import {
  Table,
  Tag,
  Input,
  Button,
  Row,
  Col,
  Typography,
  Card,
  Statistic,
  Space,
  Modal,
  Alert,
  Upload,
  message,
  Divider,
  Progress,
  Select,
  Popconfirm,
} from "antd";
import {
  EyeOutlined,
  UserAddOutlined,
  SearchOutlined,
  UploadOutlined,
  DownloadOutlined,
  FileExcelOutlined,
  IdcardOutlined,
  DeleteOutlined,
  TeamOutlined,
  CheckCircleOutlined,
  UserOutlined,
  RiseOutlined,
} from "@ant-design/icons";
import React, { useEffect, useState, useContext, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { AppContext } from "../context/AppContext";
import { TourContext } from "../context/TourContext";
import Papa from "papaparse";
import * as XLSX from "xlsx";

const { Title } = Typography;

// Zambian theme colors
const zambianColors = {
  green: "#4d8c4a",
  red: "#d16b6b",
  orange: "#e99e54",
  black: "#444444",
  white: "#FFFFFF",
  lightGreen: "#80b782",
  lightOrange: "#f4c28e",
};

// Borrowers who signed up via the mobile app within the last 30 days are
// shown with a "NEW" badge so admins can spot fresh accounts.
const NEW_BORROWER_WINDOW_DAYS = 30;
const isNewBorrower = (signupDate) => {
  if (!signupDate) return false;
  const d = new Date(signupDate);
  if (Number.isNaN(d.getTime())) return false;
  const ageMs = Date.now() - d.getTime();
  return ageMs >= 0 && ageMs <= NEW_BORROWER_WINDOW_DAYS * 24 * 60 * 60 * 1000;
};

const Borrowers = () => {
  const token = localStorage.getItem("token");
  const { company } = useContext(AppContext);
  const { runTour, tourContent, startTour, endTour } = useContext(TourContext);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [selectedCompany, setSelectedCompany] = useState(undefined);
  const [loanFilter, setLoanFilter] = useState("all"); // "all", "with_loans", "without_loans"
  const [currentPage, setCurrentPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [searchQuery, setSearchQuery] = useState(""); // Server-side search
  const [allCompanies, setAllCompanies] = useState([]); // All unique companies
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [bulkLookupModalVisible, setBulkLookupModalVisible] = useState(false);
  const [bulkLookupFile, setBulkLookupFile] = useState(null);
  const [bulkLookupLoading, setBulkLookupLoading] = useState(false);
  const [bulkLookupProgress, setBulkLookupProgress] = useState(0);
  const [deletingBorrower, setDeletingBorrower] = useState(null);
  const [totalBorrowersCount, setTotalBorrowersCount] = useState(0);
  const [activeBorrowersCount, setActiveBorrowersCount] = useState(0);
  const navigate = useNavigate();

  const handleDeleteBorrower = async (borrower) => {
    try {
      setDeletingBorrower(borrower.id);

      await axios.delete(
        `https://zedlenders.pickmesms.com/users/${borrower.id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      message.success(
        `Borrower ${borrower.first_name} ${borrower.last_name} deleted successfully`
      );

      // Reload the page after a short delay to show the success message
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (err) {
      console.error("Error deleting borrower:", err);
      message.error(
        "Failed to delete borrower: " +
          (err.response?.data?.errors?.[0]?.message || err.message)
      );
    } finally {
      setDeletingBorrower(null);
    }
  };

  const columns = [
    {
      title: "BORROWER",
      dataIndex: "first_name",
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 500, display: "flex", alignItems: "center", gap: 6 }}>
            {`${record.first_name} ${record.last_name}`}
            {isNewBorrower(record.signup_date) ? (
              <Tag color="green" style={{ margin: 0, fontSize: 10, lineHeight: "16px", padding: "0 6px" }}>
                NEW
              </Tag>
            ) : null}
          </div>
          <div style={{ color: "#8c8c8c", fontSize: 12 }}>
            NRC: {record.nrc}
          </div>
        </div>
      ),
      sorter: (a, b) => a.first_name.localeCompare(b.first_name),
    },
    {
      title: "COMPANY",
      dataIndex: "business",
      render: (text) => text || "N/A",
    },
    {
      title: "CONTACT",
      render: (_, record) => (
        <div>
          <div>{record.phone}</div>
          <div style={{ color: "#8c8c8c", fontSize: 12 }}>{record.email}</div>
        </div>
      ),
    },

    {
      title: "STATUS",
      dataIndex: "status",
      render: (status) => (
        <Tag color={status === "active" ? "green" : "volcano"}>
          {status.toUpperCase()}
        </Tag>
      ),
      filters: [
        { text: "Active", value: "active" },
        { text: "Inactive", value: "inactive" },
      ],
      onFilter: (value, record) => record.status === value,
    },
    {
      title: "LOANS",
      render: (_, record) => record.loans?.length || 0,
      sorter: (a, b) => (a.loans?.length || 0) - (b.loans?.length || 0),
    },
    {
      title: "",
      key: "action",
      render: (_, record) => {
        const hasLoans = record.loans?.length > 0;

        return (
          <Space>
            <Button
              type="link"
              icon={<EyeOutlined />}
              onClick={() => navigate(`/borrower/${record.id}`)}
              title="View Borrower"
            />
            {!hasLoans && (
              <Popconfirm
                title="Delete Borrower"
                description={`Are you sure you want to delete ${record.first_name} ${record.last_name}? This action cannot be undone.`}
                onConfirm={() => handleDeleteBorrower(record)}
                okText="Yes, Delete"
                cancelText="Cancel"
                okType="danger"
              >
                <Button
                  type="link"
                  icon={<DeleteOutlined />}
                  loading={deletingBorrower === record.id}
                  title="Delete Borrower"
                  danger
                />
              </Popconfirm>
            )}
            {/* <Button
              type="link"
              icon={<IdcardOutlined />}
              onClick={() => navigate(`/nrc-verification/${record.id}`)}
              title="Verify NRC"
            /> */}
          </Space>
        );
      },
    },
  ].filter((column) => !(column.dataIndex === "status" && String(company?.id) === "22"));

  // Debounced search function
  const debouncedSearch = useCallback(
    (searchValue) => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
      const timeout = setTimeout(() => {
        setSearchQuery(searchValue);
        setCurrentPage(0); // Reset to first page on search
      }, 500);
      setSearchTimeout(timeout);
    },
    [searchTimeout]
  );

  // Fetch all unique companies for filter dropdown
  useEffect(() => {
    const fetchAllCompanies = async () => {
      try {
        const companiesUrl = `https://zedlenders.pickmesms.com/users?fields=business&filter[role][name][_eq]=Borrower&filter[company]=${company.id}&filter[business][_nnull]=true&limit=1000`;
        const response = await axios.get(companiesUrl, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const uniqueBusinesses = [
          ...new Set(
            response.data.data
              .map((user) => user.business)
              .filter((business) => business && business.trim() !== "")
          ),
        ];
        setAllCompanies(uniqueBusinesses);
      } catch (err) {
        console.error("Error fetching companies:", err);
      }
    };
    if (company?.id) {
      fetchAllCompanies();
    }
  }, [company?.id]);

  useEffect(() => {
    const fetchBorrowers = async () => {
      setLoading(true);
      try {
        // Exclude mobile-app signups for now (the app has been unpublished)
        // — same "real, non-placeholder signup_date" exclusion used for the
        // Total/Active Borrowers counts below: only borrowers with no
        // signup_date at all, or the legacy "2020-01-01" bulk-import
        // placeholder, are shown.
        let url = `https://zedlenders.pickmesms.com/users?fields=*.*&filter[role][name][_eq]=Borrower&filter[company]=${
          company.id
        }&filter[_or][0][signup_date][_null]=true&filter[_or][1][signup_date][_eq]=2020-01-01&sort=-id&limit=${pageSize}&offset=${
          currentPage * pageSize
        }&meta=total_count,filter_count`;

        // Add loan filter
        if (loanFilter === "with_loans") {
          url += `&filter[loans][_nnull]=true`;
        } else if (loanFilter === "without_loans") {
          url += `&filter[loans][_null]=true`;
        }

        // Add company filter
        if (selectedCompany) {
          url += `&filter[business][_icontains]=${selectedCompany}`;
        }

        // Add server-side search
        if (searchQuery && searchQuery.trim()) {
          const encodedSearch = encodeURIComponent(searchQuery.trim());
          url += `&filter[_or][0][first_name][_icontains]=${encodedSearch}`;
          url += `&filter[_or][1][last_name][_icontains]=${encodedSearch}`;
          url += `&filter[_or][2][email][_icontains]=${encodedSearch}`;
          url += `&filter[_or][3][phone][_icontains]=${encodedSearch}`;
          url += `&filter[_or][4][nrc][_icontains]=${encodedSearch}`;
        }

        const response = await axios.get(url, {
          headers: { Authorization: `Bearer ${token}` },
        });

        setUsers(response.data.data);
        // Get total count from meta instead of separate API call
        setTotalCount(
          response.data.meta?.filter_count ||
            response.data.meta?.total_count ||
            0
        );
      } catch (err) {
        console.error("Error fetching borrowers:", err);
      } finally {
        setLoading(false);
      }
    };

    if (company?.id) {
      fetchBorrowers();
    }
  }, [
    selectedCompany,
    loanFilter,
    currentPage,
    pageSize,
    searchQuery,
    company?.id,
  ]);

  useEffect(() => {
    const fetchBorrowerSummary = async () => {
      if (!company?.id) return;
      try {
        // Exclude mobile-app signups (real signup_date, not the legacy
        // "2020-01-01" bulk-import placeholder that ~2,700 other borrowers
        // also carry) — the app has been unpublished from the Play Store.
        const excludeAppSignups =
          "&filter[_or][0][signup_date][_null]=true&filter[_or][1][signup_date][_eq]=2020-01-01";
        const totalUrl = `https://zedlenders.pickmesms.com/users?filter[role][name][_eq]=Borrower&filter[company]=${company.id}${excludeAppSignups}&limit=1&meta=filter_count`;
        const activeUrl = `https://zedlenders.pickmesms.com/users?filter[role][name][_eq]=Borrower&filter[company]=${company.id}&filter[loans][loan_status][_in]=7,8${excludeAppSignups}&limit=1&meta=filter_count`;

        const [totalRes, activeRes] = await Promise.all([
          axios.get(totalUrl, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(activeUrl, { headers: { Authorization: `Bearer ${token}` } }),
        ]);

        setTotalBorrowersCount(totalRes.data.meta?.filter_count || 0);
        setActiveBorrowersCount(activeRes.data.meta?.filter_count || 0);
      } catch (err) {
        console.error("Error fetching borrower summary:", err);
      }
    };

    fetchBorrowerSummary();
  }, [company?.id]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
    };
  }, [searchTimeout]);

  // No need for client-side filtering since we do server-side search
  const filteredData = users;

  const Text = Typography.Text;

  const handleExportBorrowers = async () => {
    try {
      if (!company?.id) return;

      message.loading({ content: "Preparing export...", key: "export" });

      // Build export URL mirroring current filters, but fetch all rows
      let url = `https://zedlenders.pickmesms.com/users?fields=*.*&filter[role][name][_eq]=Borrower&filter[company]=${company.id}&limit=-1`;

      if (loanFilter === "with_loans") {
        url += `&filter[loans][_nnull]=true`;
      } else if (loanFilter === "without_loans") {
        url += `&filter[loans][_null]=true`;
      }

      if (selectedCompany) {
        url += `&filter[business][_icontains]=${encodeURIComponent(selectedCompany)}`;
      }

      if (searchQuery && searchQuery.trim()) {
        const encodedSearch = encodeURIComponent(searchQuery.trim());
        url += `&filter[_or][0][first_name][_icontains]=${encodedSearch}`;
        url += `&filter[_or][1][last_name][_icontains]=${encodedSearch}`;
        url += `&filter[_or][2][email][_icontains]=${encodedSearch}`;
        url += `&filter[_or][3][phone][_icontains]=${encodedSearch}`;
        url += `&filter[_or][4][nrc][_icontains]=${encodedSearch}`;
      }

      const { data } = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const rows = (data?.data || []).map((b, idx) => ({
        "S/N": idx + 1,
        "First Name": b.first_name || "",
        "Last Name": b.last_name || "",
        "Full Name": `${b.first_name || ""} ${b.last_name || ""}`.trim(),
        NRC: b.nrc || "",
        Company: b.business || "",
        Phone: b.phone || "",
        Email: b.email || "",
        Status: (b.status || "").toString().toUpperCase(),
        "Loans Count": Array.isArray(b.loans) ? b.loans.length : 0,
      }));

      if (rows.length === 0) {
        message.warning({ content: "No borrowers to export", key: "export" });
        return;
      }

      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.json_to_sheet(rows);
      worksheet["!cols"] = [
        { wch: 6 }, // S/N
        { wch: 16 }, // First Name
        { wch: 16 }, // Last Name
        { wch: 24 }, // Full Name
        { wch: 18 }, // NRC
        { wch: 24 }, // Company
        { wch: 16 }, // Phone
        { wch: 26 }, // Email
        { wch: 10 }, // Status
        { wch: 12 }, // Loans Count
      ];
      XLSX.utils.book_append_sheet(workbook, worksheet, "Borrowers");

      const currentDate = new Date().toISOString().split("T")[0];
      const filename = `Borrowers_${currentDate}.xlsx`;
      XLSX.writeFile(workbook, filename);

      message.success({
        content: `Exported ${rows.length} borrowers`,
        key: "export",
      });
    } catch (err) {
      console.error("Failed to export borrowers:", err);
      message.error({ content: "Failed to export borrowers", key: "export" });
    }
  };

  return (
    <>
      {/* Tour Modal */}
      {runTour && tourContent && (
        <Modal
          title={tourContent.title || "Borrower Management Guide"}
          open={runTour}
          onCancel={() => endTour(false)}
          footer={[
            <Button key="skip" onClick={() => endTour(false)}>
              Skip
            </Button>,
            <Button key="next" type="primary" onClick={() => endTour(true)}>
              Got it!
            </Button>,
          ]}
          width={500}
        >
          <div style={{ padding: "10px 0" }}>
            <Alert
              message={tourContent.message || "Welcome to Borrower Management!"}
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />
            <Text>Managing borrowers:</Text>
            <ul>
              {tourContent.details?.map((detail, index) => (
                <li key={index}>{detail}</li>
              ))}
            </ul>
          </div>
        </Modal>
      )}

      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={6}>
          <Card
            bordered={false}
            style={{
              borderRadius: 8,
              background: zambianColors.green,
              boxShadow: "0 2px 8px rgba(77, 140, 74, 0.2)",
            }}
          >
            <Statistic
              title={
                <span style={{ color: "#fff", fontSize: 14, fontWeight: 500 }}>
                  Total Borrowers
                </span>
              }
              value={totalBorrowersCount}
              valueStyle={{ color: "#fff", fontSize: 32, fontWeight: "bold" }}
              prefix={
                <TeamOutlined style={{ color: "#fff", fontSize: 24 }} />
              }
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card
            bordered={false}
            style={{
              borderRadius: 8,
              background: zambianColors.lightGreen,
              boxShadow: "0 2px 8px rgba(128, 183, 130, 0.2)",
            }}
          >
            <Statistic
              title={
                <span style={{ color: "#fff", fontSize: 14, fontWeight: 500 }}>
                  With Active Loans
                </span>
              }
              value={activeBorrowersCount}
              valueStyle={{ color: "#fff", fontSize: 32, fontWeight: "bold" }}
              prefix={
                <CheckCircleOutlined style={{ color: "#fff", fontSize: 24 }} />
              }
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card
            bordered={false}
            style={{
              borderRadius: 8,
              background: zambianColors.orange,
              boxShadow: "0 2px 8px rgba(233, 158, 84, 0.2)",
            }}
          >
            <Statistic
              title={
                <span style={{ color: "#fff", fontSize: 14, fontWeight: 500 }}>
                  Without Loans
                </span>
              }
              value={totalBorrowersCount - activeBorrowersCount}
              valueStyle={{ color: "#fff", fontSize: 32, fontWeight: "bold" }}
              prefix={
                <UserOutlined style={{ color: "#fff", fontSize: 24 }} />
              }
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card
            bordered={false}
            style={{
              borderRadius: 8,
              background: zambianColors.black,
              boxShadow: "0 2px 8px rgba(68, 68, 68, 0.2)",
            }}
          >
            <Statistic
              title={
                <span style={{ color: "#fff", fontSize: 14, fontWeight: 500 }}>
                  Active Loan Rate
                </span>
              }
              value={
                totalBorrowersCount > 0
                  ? ((activeBorrowersCount / totalBorrowersCount) * 100).toFixed(1)
                  : 0
              }
              valueStyle={{ color: "#fff", fontSize: 32, fontWeight: "bold" }}
              prefix={
                <RiseOutlined style={{ color: "#fff", fontSize: 24 }} />
              }
              suffix={<span style={{ color: "#fff", fontSize: 18 }}>%</span>}
            />
          </Card>
        </Col>
      </Row>

      <Card bordered={false} style={{ padding: 24 }}>
        <Row
          justify="space-between"
          align="middle"
          style={{ marginBottom: 24 }}
        >
          <Col>
            <Title level={4} className="borrowers-title" style={{ margin: 0 }}>
              Borrower Management
            </Title>
          </Col>
          <Col>
            <Space>
              <Select
                placeholder="Filter by loan status"
                style={{ width: 150 }}
                value={loanFilter}
                onChange={setLoanFilter}
              >
                <Select.Option value="all">All Borrowers</Select.Option>
                <Select.Option value="with_loans">With Loans</Select.Option>
                <Select.Option value="without_loans">No Loans</Select.Option>
              </Select>
              <Select
                placeholder="Filter by company"
                style={{ width: 200 }}
                allowClear
                value={selectedCompany}
                onChange={setSelectedCompany}
              >
                {allCompanies.map((business) => (
                  <Select.Option key={business} value={business}>
                    {business}
                  </Select.Option>
                ))}
              </Select>
              <Input
                placeholder="Search borrowers..."
                prefix={<SearchOutlined />}
                onChange={(e) => {
                  setSearchText(e.target.value);
                  debouncedSearch(e.target.value);
                }}
                value={searchText}
                style={{ width: 300 }}
                className="borrowers-search"
                allowClear
              />
              <Button
                type="primary"
                className="bulk-lookup-button"
                icon={<FileExcelOutlined />}
                onClick={() => setBulkLookupModalVisible(true)}
                style={{ marginRight: 8 }}
              >
                Bulk Lookup
              </Button>
              <Button
                type="default"
                icon={<DownloadOutlined />}
                onClick={handleExportBorrowers}
              >
                Export
              </Button>
              <Button
                type="primary"
                className="new-borrower-button"
                icon={<UserAddOutlined />}
                onClick={() => navigate("/addBorrower")}
              >
                New Borrower
              </Button>
            </Space>
          </Col>
        </Row>

        <Table
          columns={columns}
          dataSource={filteredData}
          rowKey="id"
          loading={loading}
          pagination={{
            current: currentPage + 1,
            pageSize: pageSize,
            total: totalCount,
            showSizeChanger: true,
            pageSizeOptions: ["10", "25", "50", "100"],
            showTotal: (total, range) => {
              if (searchQuery) {
                return `${range[0]}-${range[1]} of ${total} borrowers (filtered)`;
              }
              return `${range[0]}-${range[1]} of ${total} borrowers`;
            },
            onChange: (page, size) => {
              setCurrentPage(page - 1);
              if (size !== pageSize) {
                setPageSize(size);
                setCurrentPage(0);
              }
            },
            showQuickJumper: true,
          }}
          scroll={{ x: true }}
          bordered
        />
      </Card>
      {/* Bulk Lookup Modal */}
      <Modal
        title="Bulk Phone Number Lookup"
        open={bulkLookupModalVisible}
        onCancel={() => {
          setBulkLookupModalVisible(false);
          setBulkLookupFile(null);
          setBulkLookupProgress(0);
        }}
        footer={[
          <Button
            key="cancel"
            onClick={() => {
              setBulkLookupModalVisible(false);
              setBulkLookupFile(null);
              setBulkLookupProgress(0);
            }}
          >
            Cancel
          </Button>,
          <Button
            key="submit"
            type="primary"
            icon={<UploadOutlined />}
            loading={bulkLookupLoading}
            disabled={!bulkLookupFile || bulkLookupLoading}
            onClick={handleBulkLookup}
          >
            Process Lookup
          </Button>,
        ]}
        width={600}
      >
        <Alert
          message="Warning"
          description="Only up to 100 phone numbers can be processed at once."
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />
        <Divider orientation="left">Upload CSV File</Divider>
        <p>
          Upload a CSV file with phone numbers. The file should have the
          following format:
        </p>
        <pre style={{ background: "#f5f5f5", padding: 16, borderRadius: 4 }}>
          account,provider
          <br />
          0762200229,MTN
          <br />
          0977123456,Airtel
          <br />
          0955789012,Zamtel
        </pre>
        <p>
          The 'provider' column is optional. If omitted, the system will attempt
          to detect the provider based on the account number.
        </p>
        <Alert
          message="Important: Phone Number Format"
          description="Leading zeros in phone numbers will be preserved automatically. You don't need to add any special formatting."
          type="info"
          showIcon
          style={{ marginTop: 16, marginBottom: 16 }}
        />

        <Upload
          accept=".csv"
          beforeUpload={(file) => {
            const isCsv =
              file.type === "text/csv" || file.name.endsWith(".csv");
            if (!isCsv) {
              message.error("You can only upload CSV files!");
              return Upload.LIST_IGNORE;
            }
            setBulkLookupFile(file);
            return false;
          }}
          onRemove={() => {
            setBulkLookupFile(null);
          }}
          fileList={bulkLookupFile ? [bulkLookupFile] : []}
          maxCount={1}
        >
          <Button icon={<UploadOutlined />}>Select CSV File</Button>
        </Upload>

        {bulkLookupLoading && (
          <div style={{ marginTop: 16 }}>
            <Progress percent={bulkLookupProgress} status="active" />
            <p style={{ textAlign: "center" }}>Processing your request...</p>
          </div>
        )}
      </Modal>
    </>
  );

  async function handleBulkLookup() {
    if (!bulkLookupFile) {
      message.error("Please select a CSV file first");
      return;
    }

    setBulkLookupLoading(true);
    setBulkLookupProgress(15);

    try {
      // Read and parse the CSV file using PapaParse to preserve leading zeros
      const parseResult = await new Promise((resolve, reject) => {
        Papa.parse(bulkLookupFile, {
          header: true,
          complete: resolve,
          error: reject,
          skipEmptyLines: true,
        });
      });

      setBulkLookupProgress(30);

      // Extract the phone numbers and providers
      const accounts = parseResult.data
        .map((row) => {
          // Make sure phone numbers are treated as strings to preserve leading zeros
          return {
            account: row.account ? String(row.account) : null,
            provider: row.provider || null,
          };
        })
        .filter((item) => item.account); // Filter out any empty entries

      if (accounts.length === 0) {
        throw new Error("No valid phone numbers found in the CSV file");
      }

      if (accounts.length > 100) {
        message.warning("Only the first 100 phone numbers will be processed");
      }

      setBulkLookupProgress(50);

      // Create JSON payload instead of sending the file directly
      const response = await axios.post(
        "http://flowmatic.solutions/api/bulk-lookup?format=csv",
        { accounts: accounts.slice(0, 100) }, // Limit to 100 accounts
        {
          headers: {
            "Content-Type": "application/json",
            "X-API-Key":
              process.env.REACT_APP_LOOKUP_API_KEY ||
              "6d8d99a0cde85b88ddf2649f0ee78d57",
          },
          responseType: "blob", // Important: sets the response type as blob
        }
      );

      setBulkLookupProgress(75);

      // Create a download link for the CSV file
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `bulk-lookup-results-${new Date().toISOString().slice(0, 10)}.csv`
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setBulkLookupProgress(100);
      message.success("Bulk lookup completed successfully!");

      // Close the modal after a short delay
      setTimeout(() => {
        setBulkLookupModalVisible(false);
        setBulkLookupFile(null);
        setBulkLookupProgress(0);
        setBulkLookupLoading(false);
      }, 1500);
    } catch (error) {
      console.error("Error performing bulk lookup:", error);
      message.error("Failed to process bulk lookup. Please try again.");
      setBulkLookupLoading(false);
      setBulkLookupProgress(0);
    }
  }
};

export default Borrowers;
